import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { DwComponent, DwComponentType, DwExpressionService, DwMetaDataFormApi, DwMetaDataFormSectionStateService, DwMetaDataFormStateService, DwMetaDataService, DwMetaDataServiceToken, DwSectionReadonlyBaseComponent, DwUiConfigRegistryService, DwUIMetaDataConfig, DwUIMetaDataConfigToken } from '@devwareapps/devware-cap';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AppMetaData, AppMetaDataItemNames, CourseDocumentItemEntity, CourseEntity, DocumentGroupAllItems, DocumentItemStatusAllItems, DocumentTypeAllItems, ReferenceStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { DocumentsRepositoryService } from '../../../documents/services/documents-repository.service';

interface DocumentOption {
  label: string;
  value: number;
  detail?: string;
}

@DwComponent({
  key: 'course-document-link-form-section',
  name: 'Course Document Link Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.Course,
  isGlobal: false,
})
@Component({
  selector: 'app-course-document-link-form-section',
  template: `
    <div class="dw-form-field">
      <label class="label">Documents</label>
      <p-multiSelect
        *ngIf="!isSectionReadOnly(); else readonly"
        [options]="documentOptions"
        [(ngModel)]="selectedDocumentIds"
        [ngModelOptions]="{ standalone: true }"
        optionLabel="label"
        optionValue="value"
        defaultLabel="Select documents"
        display="chip"
        [filter]="true"
        [showToggleAll]="false"
        [disabled]="!associativeList"
        [style]="{'width':'100%'}"
        appendTo="body"
        baseZIndex="2000"
        autoZIndex="false"
        [ngClass]="{ 'multi-edit-auto-expand': true }"
        (ngModelChange)="updateSelectedDocuments()">
      </p-multiSelect>
      <small *ngIf="!isSectionReadOnly()" class="dw-text-muted">
        Removing a document marks its course link inactive so it can be restored later.
      </small>
    </div>
    <ng-template #readonly>
      <div [hidden]="!displayItems.length" class="dw-chip-container">
        <ng-container *ngFor="let item of displayItems; let i = index">
          <div class="dw-chip">
            <span class="dw-chip-label">{{ item }}</span>
          </div>
        </ng-container>
      </div>
      <div [hidden]="displayItems.length > 0">
        None
      </div>
    </ng-template>
  `
})
export class CourseDocumentLinkFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  associativeList?: AbstractControl;
  formApi: DwMetaDataFormApi;
  selectedDocumentIds: number[] = [];
  documentOptions: DocumentOption[] = [];
  displayItems: string[] = [];

  private readonly controlName = 'CourseDocumentItem';
  private readonly destroy$ = new Subject<void>();

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService,
    private documentsRepositoryService: DocumentsRepositoryService
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.associativeList = this.formGroup.get(this.controlName);

    if (!this.associativeList) {
      console.warn(`Form control ${this.controlName} not found in form group.`);
      return;
    }

    this.runAfterFormDataLoaded(() => {
      this.loadDocumentOptions();
      this.syncSelectedDocuments();
    });

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncSelectedDocuments());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    return;
  }

  updateSelectedDocuments(): void {
    const course = this.formApi.getFormData<CourseEntity>();
    const allLinks = [...((this.associativeList?.value || []) as CourseDocumentItemEntity[])];

    allLinks
      .filter(link => link.CourseDocumentItemStatusId !== ReferenceStatusAllItems.Deleted)
      .forEach(link => {
        link._isDeleted = false;
        link.CourseDocumentItemStatusId = ReferenceStatusAllItems.Inactive;
      });

    for (const documentItemId of this.selectedDocumentIds) {
      let existingLink = allLinks.find(link =>
        link.DocumentItemId === documentItemId
        && link.CourseDocumentItemStatusId !== ReferenceStatusAllItems.Deleted);

      if (!existingLink) {
        existingLink = AppMetaData.CourseDocumentItem.CreateEntity();
        existingLink.CourseId = course?.CourseId;
        existingLink.DocumentItemId = documentItemId;
        allLinks.push(existingLink);
      }

      existingLink._isDeleted = false;
      existingLink.CourseDocumentItemStatusId = ReferenceStatusAllItems.Active;
    }

    this.associativeList?.setValue(allLinks);
  }

  private loadDocumentOptions(): void {
    this.documentsRepositoryService.getDocumentGroup(DocumentGroupAllItems.Documents)
      .pipe(takeUntil(this.destroy$))
      .subscribe(documentGroup => {
        this.documentOptions = (documentGroup?.DocumentItem || [])
          .filter(documentItem => documentItem.DocumentItemStatusId === DocumentItemStatusAllItems.Active)
          .filter(documentItem => documentItem.DocumentTypeId !== DocumentTypeAllItems.SketchFab3D)
          .filter(documentItem => !!documentItem.DocumentItemId)
          .sort((a, b) => (a.DocumentItemName || '').localeCompare(b.DocumentItemName || ''))
          .map(documentItem => ({
            label: documentItem.DocumentItemName || `Document ${documentItem.DocumentItemId}`,
            value: documentItem.DocumentItemId!,
            detail: documentItem.DocumentTypeIdDisplay
          }));

        this.syncSelectedDocuments();
      });
  }

  private syncSelectedDocuments(): void {
    const activeDocumentIds = (((this.associativeList?.value || []) as CourseDocumentItemEntity[])
      .filter(link => link.CourseDocumentItemStatusId === ReferenceStatusAllItems.Active && link._isDeleted !== true)
      .map(link => link.DocumentItemId)
      .filter((documentItemId): documentItemId is number => !!documentItemId));

    this.selectedDocumentIds = [...activeDocumentIds];
    this.displayItems = this.selectedDocumentIds
      .map(documentItemId => this.documentOptions.find(option => option.value === documentItemId)?.label || `Document ${documentItemId}`)
      .filter((label): label is string => !!label);
  }
}
